import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Users, Lightbulb, Briefcase, FileText, Building, Target, BookOpen, Zap, Shield, TrendingUp, UserCheck, Brain } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { markWelcomeSeen } from '../services/profileService';
import { setWelcomeSeen } from '../redux/authSlice';

const WelcomePage = () => {
  const { firstName } = useSelector((state) => state.auth);
  const displayName = firstName || "Alex";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    // Mark welcome page as seen when component mounts
    const updateWelcomeStatus = async () => {
      try {
        await markWelcomeSeen();
        // Update Redux state to reflect that welcome has been seen
        dispatch(setWelcomeSeen(true));
      } catch (error) {
        console.error('Error marking welcome as seen:', error);
        // Silently fail - this is not critical to user experience
      }
    };

    updateWelcomeStatus();
  }, [dispatch]);

  const howItWorksSteps = [
    { number: 1, title: "View AI Job Impact", description: "See how AI is shaping your current role." },
    { number: 2, title: "Explore AI Proof Careers", description: "Discover AI-proof career options tailored for you." },
    { number: 3, title: "Select Target Career", description: "Choose your pathway for focused growth." },
    { number: 4, title: "Assess Your AI Literacy", description: "Test your AI readiness and skills." },
    { number: 5, title: "Build Upskilling Plan", description: "Future-proof your skills with a personalized plan." }
  ];

  const unlockFeatures = [
    { icon: <UserCheck className="w-6 h-6 text-white" />, title: "Mentorship", description: "1-on-1 guidance from industry experts." },
    { icon: <Users className="w-6 h-6 text-white" />, title: "Community", description: "Join a network of like-minded peers." },
    { icon: <Brain className="w-6 h-6 text-white" />, title: "Career Tools", description: "Access Career Simulator, Skills Gap Analysis, Interview Prep and more." },
    { icon: <Briefcase className="w-6 h-6 text-white" />, title: "Jobs", description: "Discover AI-ready job opportunities." },
    { icon: <FileText className="w-6 h-6 text-white" />, title: "AI Talent Pool", description: "Make your Upskilling Plan, AI Literacy results (your continuous learning resume) visible to employers." }
  ];

  const StepCard = ({ step }) => (
    <div className="border border-gray-300 rounded-xl p-4 text-center" style={{ backgroundColor: '#fff0fa' }}>
      <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
        <span className="text-white font-bold text-lg">{step.number}</span>
      </div>
      <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
      <p className="text-gray-500 text-sm">{step.description}</p>
    </div>
  );

  const FeatureCard = ({ feature }) => (
    <div className="border border-gray-300 rounded-xl p-4 text-center transition-colors duration-200" style={{ backgroundColor: '#fff0fa' }}>
      <div className="w-12 h-12 bg-purple-600 rounded-full mx-auto mb-3 flex items-center justify-center">
        {feature.icon}
      </div>
      <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
      <p className="text-gray-500 text-sm">{feature.description}</p>
    </div>
  );

  return (
    <Sidebar>
      <div className="bg-white p-6">
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="text-center mb-8 px-4">
            <h1 className="text-3xl font-bold text-blue-900 mb-4">
              Welcome to Ontrack Careers, {displayName}!
            </h1>
            <p className="text-lg text-black max-w-4xl mx-auto leading-relaxed">
              Transform your career for the AI era. Assess risks, discover opportunities, plan your upskilling journey, and get the support you need to succeed.
            </p>
          </div>

          {/* How It Works */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-center text-blue-900 mb-8">How It Works</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {howItWorksSteps.map((step, index) => (
                <StepCard key={index} step={step} />
              ))}
            </div>
          </div>

          {/* What You'll Unlock */}
          <div className="mb-16 border border-gray-400 p-4 rounded-lg hover:shadow-xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">What You'll Unlock</h2>
              <p className="text-base text-gray-500 max-w-3xl mx-auto">
                As you progress, gain access to tools and support designed to help you succeed.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {unlockFeatures.map((feature, index) => (
                <FeatureCard key={index} feature={feature} />
              ))}
            </div>
            <div className="text-center mt-5">
              <button
                onClick={() => navigate('/dashboard/home')}
                className="bg-white border rounded-lg border-blue-600 text-blue-600 font-semibold text-lg py-1 px-4 text-lg hover:bg-blue-50 transition-all duration-300"
              >
                Start My AI-Proof Career Plan
              </button>
            </div>
          </div>

        </div>
      </div>
    </Sidebar>
  );
};

export default WelcomePage; 